import React from "react";
import { Link } from "react-router-dom";
import { StarIcon, ArrowRight } from "./icons";
import { PACKAGE_IMAGES } from "./packageImages";

const DEFAULT_BOXES = [
  { key: "bow-tie-lite",       label: "Bow Tie Lite package" },
  { key: "classic-knot",       label: "Classic Knot package" },
  { key: "windsor-growth",     label: "Windsor Growth package" },
  { key: "black-label-studio", label: "Black Label Studio package" },
];

export default function Hero({
  onPrimaryCta,
  primaryCtaText = "See Plans",
  primaryCtaHref = "#pricing",
  primaryCtaTo,
  primaryCtaOnClick,
  // Optional 2nd primary CTA — used on /book-a-call so visitors see both
  // "Free Consultation" and "Book Strategy Call" up top, both anchoring to
  // the audit-packages section.
  primary2CtaText,
  primary2CtaHref,
  primary2CtaTo,
  primary2CtaOnClick,
  secondaryCtaText = "No thanks, build me a funnel instead",
  secondaryCtaTo = "/funnels",
  secondaryCtaOnClick,
  badge = "Bow Tie Kreative · Websites",
  singleImage = null,
  singleImageAlt = "",
  headline = (
    <>
      Build a <span className="bk-gold-text italic font-medium">Website</span><br />
      That Works as <span className="bk-gold-text italic font-medium">Hard</span><br />
      as You Do
    </>
  ),
  subheadline = (
    <>
      Stop putting up with a <span className="text-white/85">"pretty but passive"</span> site.
      Get a custom-built website designed for conversions, clarity, and momentum —
      without the agency price tag or DIY headaches.
    </>
  ),
  boxes = DEFAULT_BOXES,
  imagesMap = PACKAGE_IMAGES,
  ratingCopy = (
    <><span className="text-white">5.0</span> from 200+ verified clients</>
  ),
}) {
  return (
    <header
      id="top"
      data-testid="hero"
      className="relative bk-glow pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center relative z-10">
        <div className="lg:col-span-7 animate-fade-up">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold/90 px-4 py-1.5 rounded-full border border-gold/30 bg-black/40 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            {badge}
          </span>

          <h1 className="mt-8 font-display text-[clamp(2.4rem,6vw,4.5rem)] leading-[1.05] tracking-tight">
            {headline}
          </h1>

          <p className="mt-7 text-base md:text-lg text-white/65 max-w-xl leading-relaxed">
            {subheadline}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            {primaryCtaTo ? (
              <Link
                to={primaryCtaTo}
                onClick={primaryCtaOnClick}
                data-testid="hero-see-plans"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.22em] font-bold hover:scale-[1.03] transition-transform"
              >
                {primaryCtaText}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : (
              <a
                href={primaryCtaHref}
                onClick={primaryCtaOnClick}
                data-testid="hero-see-plans"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.22em] font-bold hover:scale-[1.03] transition-transform"
              >
                {primaryCtaText}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            )}
            {primary2CtaText ? (
              primary2CtaTo ? (
                <Link
                  to={primary2CtaTo}
                  onClick={primary2CtaOnClick}
                  data-testid="hero-primary2-cta"
                  className="group inline-flex items-center gap-2 px-7 py-4 rounded-full border border-gold/50 bg-black/30 text-gold text-xs uppercase tracking-[0.22em] font-bold hover:bg-gold/10 hover:border-gold transition-colors"
                >
                  {primary2CtaText}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              ) : (
                <a
                  href={primary2CtaHref || "#"}
                  onClick={primary2CtaOnClick}
                  data-testid="hero-primary2-cta"
                  className="group inline-flex items-center gap-2 px-7 py-4 rounded-full border border-gold/50 bg-black/30 text-gold text-xs uppercase tracking-[0.22em] font-bold hover:bg-gold/10 hover:border-gold transition-colors"
                >
                  {primary2CtaText}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              )
            ) : null}
            {secondaryCtaTo ? (
              <Link
                to={secondaryCtaTo}
                onClick={onPrimaryCta}
                data-testid="hero-funnel-cta"
                className="text-sm text-white/65 hover:text-gold transition-colors underline-offset-4 hover:underline"
              >
                {secondaryCtaText} →
              </Link>
            ) : secondaryCtaOnClick ? (
              <button
                type="button"
                onClick={secondaryCtaOnClick}
                data-testid="hero-funnel-cta"
                className="text-sm text-white/65 hover:text-gold transition-colors underline-offset-4 hover:underline"
              >
                {secondaryCtaText} →
              </button>
            ) : null}
          </div>

          <div className="mt-10 flex items-center gap-3" data-testid="hero-rating">
            <div className="flex">
              {[0,1,2,3,4].map((i) => <StarIcon key={i} className="w-5 h-5 bk-star" />)}
            </div>
            <span className="text-sm text-white/60">{ratingCopy}</span>
          </div>
        </div>

        <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "200ms" }}>
          <div className="relative max-w-2xl mx-auto lg:ml-auto lg:mr-0">
            <div className="absolute -inset-10 -z-10 rounded-[3rem] bg-gradient-to-br from-gold/15 via-transparent to-transparent blur-3xl" />
            {singleImage ? (
              <div className="relative">
                <img
                  src={singleImage}
                  alt={singleImageAlt}
                  data-testid="hero-single-image"
                  className="w-full max-w-xl mx-auto rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)] animate-float"
                  loading="eager"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 md:gap-5">
                {boxes.map((b, i) => {
                  const cls = i % 2 === 0
                    ? "-rotate-2 hover:-translate-y-2 hover:rotate-0"
                    : "rotate-2 translate-y-4 hover:-translate-y-1 hover:rotate-0";
                  return (
                    <img
                      key={b.key}
                      src={imagesMap[b.key]}
                      alt={b.label}
                      data-testid={`hero-box-${b.key}`}
                      className={`w-full rounded-xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.85)] transition-transform duration-500 ${cls}`}
                      loading="eager"
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 top-20 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
    </header>
  );
}
